import React, { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';
import { useAuth0 } from '@auth0/auth0-react';
import { fetchPlants } from '../services/plantsService';
import authorizationService from '../services/authorizationService';

const PlantSelectorContainer = styled.div`
  position: relative;
  display: inline-block;
`;

const PlantButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  background: linear-gradient(180deg, #131A1B 0%, #0D0D0D 100%);
  border: 1px solid #2B3A3D;
  border-radius: 4px;
  color: #D9D9D9;
  font-size: 11px;
  font-family: 'Space Grotesk', sans-serif;
  font-weight: 400;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 1px 2px 4px rgba(0, 0, 0, 0.35) inset;
  
  &:hover:not(:disabled) {
    background: linear-gradient(180deg, #1A2324 0%, #131313 100%);
    border-color: #3A4B4E;
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
  
  svg {
    transition: transform 0.2s ease;
  }
  
  &.open svg {
    transform: rotate(180deg);
  }
`;

const DropdownMenu = styled.div`
  position: absolute;
  top: 100%;
  right: 0;
  margin-top: 4px;
  background: #1E2732;
  border: 1px solid #2B3A3D;
  border-radius: 4px;
  box-shadow: 0px 4px 12px rgba(0, 0, 0, 0.3);
  min-width: 200px;
  z-index: 1000;
  overflow: hidden;
`;

const MenuSection = styled.div`
  border-bottom: 1px solid #2B3A3D;
  
  &:last-child {
    border-bottom: none;
  }
`;

const MenuSectionHeader = styled.div`
  padding: 8px 12px;
  background: #253D3F;
  color: #B3B3B3;
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const PlantOption = styled.div`
  padding: 8px 12px;
  color: #E6E6E6;
  font-size: 12px;
  cursor: pointer;
  transition: background-color 0.2s ease;
  
  &:hover {
    background-color: #2B3A3D;
  }
`;

const LogoutOption = styled.div`
  padding: 8px 12px;
  color: #FF6B6B;
  font-size: 12px;
  cursor: pointer;
  transition: background-color 0.2s ease;
  
  &:hover {
    background-color: #2B3A3D;
  }
`;

const LoadingText = styled.div`
  padding: 12px;
  color: #B3B3B3;
  font-size: 12px;
  text-align: center;
`;

const ErrorText = styled.div`
  padding: 12px;
  color: #FF6B6B;
  font-size: 12px;
  text-align: center;
`;

const PlantSelector = ({ onPlantSelect, selectedPlant: selectedPlantProp }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [plants, setPlants] = useState([]);
  const [accessiblePlants, setAccessiblePlants] = useState([]);
  const [selectedPlant, setSelectedPlant] = useState(selectedPlantProp || null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isAuthorized, setIsAuthorized] = useState(false);
  const dropdownRef = useRef(null);
  const { getAccessTokenSilently, logout } = useAuth0();

  // Update local state when prop changes
  useEffect(() => {
    setSelectedPlant(selectedPlantProp);
  }, [selectedPlantProp]);

  useEffect(() => {
    const initializeAuthorization = async () => {
      try {
        setIsLoading(true);
        setError(null);
        
        const token = await getAccessTokenSilently();
        
        // Initialize authorization service
        const authorized = await authorizationService.initialize(token);
        setIsAuthorized(authorized);
        
        if (authorized) {
          // Fetch all plants
          const plantsData = await fetchPlants(token);
          setPlants(plantsData);
          
          // Filter plants based on user's authorization
          const userAccessiblePlants = authorizationService.getAccessiblePlants(plantsData);
          setAccessiblePlants(userAccessiblePlants);
          
          // If user has access to only one plant, auto-select it
          if (userAccessiblePlants.length === 1) {
            handlePlantSelect(userAccessiblePlants[0]);
          }
        } else {
          setError('You do not have access to any plants. Please contact your administrator.');
        }
      } catch (err) {
        console.error('Error initializing authorization:', err);
        setError('Failed to load plants. Please try again.');
      } finally {
        setIsLoading(false);
      }
    };

    initializeAuthorization();
  }, [getAccessTokenSilently]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handlePlantSelect = (plant) => {
    setSelectedPlant(plant);
    setIsOpen(false);
    onPlantSelect(plant);
  };

  const handleLogout = () => {
    setIsOpen(false);
    logout({ 
      logoutParams: {
        returnTo: window.location.origin
      }
    });
  };

  // If user is not authorized, show error message
  if (!isAuthorized && !isLoading) {
    return (
      <PlantSelectorContainer>
        <PlantButton disabled style={{ opacity: 0.6, cursor: 'not-allowed' }}>
          Access Denied
        </PlantButton>
      </PlantSelectorContainer>
    );
  }

  return (
    <PlantSelectorContainer ref={dropdownRef}>
      <PlantButton 
        onClick={() => setIsOpen(!isOpen)} 
        disabled={!isAuthorized}
        className={isOpen ? 'open' : ''}
      >
        {selectedPlant ? selectedPlant.name : 'Select Plant'}
        <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M2 4L6 8L10 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      </PlantButton>
      
      {isOpen && (
        <DropdownMenu>
          <MenuSection>
            <MenuSectionHeader>Plants</MenuSectionHeader>
            {isLoading ? (
              <LoadingText>Loading plants...</LoadingText>
            ) : error ? (
              <ErrorText>{error}</ErrorText>
            ) : accessiblePlants.length === 0 ? (
              <LoadingText>No plants available for your access level</LoadingText>
            ) : (
              accessiblePlants.map((plant) => (
                <PlantOption
                  key={plant.id}
                  onClick={() => handlePlantSelect(plant)}
                >
                  {plant.name}
                </PlantOption>
              ))
            )}
          </MenuSection>
          
          {isAuthorized && (
            <MenuSection>
              <LogoutOption onClick={handleLogout}>
                Logout
              </LogoutOption>
            </MenuSection>
          )}
        </DropdownMenu>
      )}
    </PlantSelectorContainer>
  );
};

export default PlantSelector; 